// React
import PropTypes from "prop-types";

// @mui material components
import { Card, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { useContext, useEffect, useState } from "react";
import dataCategorias from "../data/dataCategorias";
import ModalCategoria from "../ModalCategoria";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "60%", xxl: "50%" },
  overflowY: "auto",
};

export default function ModalCategorias({ open, handleClose, refetchCategorias }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "categoria",
    orden: "ASC",
  });
  const [selectedCategoria, setSelectedCategoria] = useState(null);

  const [modalCategoria, setModalCategoria] = useState(false);

  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);

  const { columns, getRows } = dataCategorias;

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };
  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [getCategorias, { loading }] = useLazyQuery(
    gql`
      query getCategorias(
        $pagination: PaginationInput
        $order: Order
        $search: String
        $filter: JSON
      ) {
        paginationInfo {
          pages
          total
        }
        categoriasProducto(
          pagination: $pagination
          order: $order
          search: $search
          filter: $filter
        ) {
          id
          categoria
          file
          icono
          categoriaPermanente
        }
      }
    `,
    {
      variables: {
        search: search || null,
        pagination: { page: page, limit: 20 },
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "network-only",
    }
  );
  const [deleteCategoria, { loading: loadingDeleteCategoria }] = useMutation(
    gql`
      mutation deleteCategoria($id: ID!) {
        deleteCategoria(id: $id) {
          id
        }
      }
    `
  );

  const ejecutarAccionUsuario = (accion, item) => {
    switch (accion) {
      case "editar":
        setModalCategoria(true);
        setSelectedCategoria(item);
        break;
      case "borrar":
        if (!loadingDeleteCategoria) {
          confirmar({
            title: "Advertencia",
            message:
              "¿Está seguro que desea borrar? Si tiene productos cargados con esta categoría deberá editarlos manualmente y elegirles una nueva categoría. Esta acción no se puede deshacer.",
            icon: "warning",
            func: () => {
              deleteCategoria({ variables: { id: item.id } })
                .then(() => {
                  handleSnackbar("Eliminado correctamente", "success");
                  getCategorias().then((res) => {
                    setRows(getRows(res.data.categoriasProducto, ejecutarAccionUsuario));
                    setPagesInfo(res.data.paginationInfo);
                  });
                })
                .catch(() => {
                  handleSnackbar("Ha ocurrido un error al borrar", "error");
                });
            },
          });
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (open) {
      getCategorias().then((res) => {
        setRows(getRows(res.data.categoriasProducto, ejecutarAccionUsuario));
        setPagesInfo(res.data.paginationInfo);
      });
    }
  }, [search, page, orderBy, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <Card>
              <Grid container spacing={2} p={3}>
                <Grid item xs={12}>
                  <SoftBox
                    display={{
                      xs: "flex-row",
                      sm: "flex",
                    }}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                      <SoftTypography variant="h6">Categorías</SoftTypography>
                      <SoftBox
                        display={{
                          xs: "block",
                          sm: "none",
                        }}
                      >
                        <Tooltip title="Agregar nueva sede" placement="top">
                          <SoftButton
                            color="primary"
                            circular
                            iconOnly
                            onClick={() => setModalCategoria(true)}
                          >
                            <Icon>add</Icon>
                          </SoftButton>
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="end"
                      mt={{
                        xs: 2,
                        sm: 0,
                      }}
                    >
                      <SoftBox
                        mr={{
                          xs: 0,
                          sm: 2,
                        }}
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "auto",
                          },
                        }}
                      >
                        <SoftInput
                          placeholder="Escribe aquí..."
                          icon={{ component: "search", direction: "left" }}
                          onChange={handleSearch}
                        />
                      </SoftBox>
                      <SoftBox
                        display={{
                          xs: "none",
                          sm: "block",
                        }}
                      >
                        <Tooltip title="Agregar nueva categoría" placement="top">
                          <SoftButton
                            color="primary"
                            circular
                            onClick={() => setModalCategoria(true)}
                          >
                            <Icon>add</Icon>
                            &nbsp;Agregar
                          </SoftButton>
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                </Grid>
              </Grid>
              <SoftBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                  width: "100%",
                }}
              >
                {loading ? (
                  <Loading />
                ) : rows?.length > 0 ? (
                  <DataTable
                    columns={columns}
                    rows={rows}
                    order={orderBy}
                    setOrder={handleOrderBy}
                  />
                ) : (
                  <SinDatos />
                )}
              </SoftBox>
              <CustomPagination
                loading={loading}
                length={rows?.length}
                total={pagesInfo?.total}
                pages={pagesInfo?.pages}
                page={page}
                setPage={setPage}
                noCard
              />
            </Card>
            <ModalCategoria
              open={modalCategoria}
              handleClose={() => {
                setModalCategoria(false);
                setSelectedCategoria(null);
              }}
              refetch={() => {
                refetchCategorias();
                getCategorias().then((res) => {
                  setRows(getRows(res.data.categoriasProducto, ejecutarAccionUsuario));
                  setPagesInfo(res.data.paginationInfo);
                });
              }}
              selectedCategoria={selectedCategoria}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCategorias.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  refetchCategorias: PropTypes.func,
};
